/**
 * Core Type Definitions
 * Industry-grade type definitions for NotesScape application
 *
 * @module types
 */

// =============================================================================
// Enums
// =============================================================================

/**
 * All available note types in the canvas
 */
export enum NoteType {
  TEXT = 'TEXT',
  IMAGE = 'IMAGE',
  LIST = 'LIST',
  TODO = 'TODO',
  PLANNER = 'PLANNER',
  EVENT = 'EVENT',
  QUOTE = 'QUOTE',
  PROFILE = 'PROFILE',
  WEATHER = 'WEATHER',
  PRICING = 'PRICING',
  PYTHON = 'PYTHON',
  FRAME = 'FRAME',
  FILE = 'FILE',
  VIDEO = 'VIDEO',
  STICKER = 'STICKER',
  TABLE = 'TABLE',
  STICKY = 'STICKY',
  SHAPE = 'SHAPE',
  BOARD = 'BOARD',
  GROUP = 'GROUP',
  QRCODE = 'QRCODE',
  POLL = 'POLL',
  CODE = 'CODE',
  MATH = 'MATH',
  LINK = 'LINK',
  EMBED = 'EMBED',
}

// =============================================================================
// Primitive Types
// =============================================================================

export type ColorTheme =
  | 'orange'
  | 'yellow'
  | 'blue'
  | 'white'
  | 'beige'
  | 'green'
  | 'teal'
  | 'purple'
  | 'red'
  | 'dark'
  | 'frame';

export type CanvasBackground = 'none' | 'dots' | 'lines' | 'grid' | 'crosshatch' | 'isometric';
export type DrawingTool = 'pen' | 'pencil' | 'highlighter' | 'eraser' | 'laser';

export interface ContextMenuState {
  x: number;
  y: number;
  noteId?: string;
  edgeId?: string;
  linkedNotes?: { id: string; title: string; edgeId: string }[];
  noteType?: NoteType;
  isLocked?: boolean;
}

export type AppTheme = 'light' | 'dark' | 'system';

export type CanvasMode = 'select' | 'hand' | 'draw' | 'comment';

// =============================================================================
// Video Sync Types (Watch Party)
// =============================================================================

/**
 * Video playback state for synchronized viewing
 */
export interface VideoSyncState {
  noteId: string;
  isPlaying: boolean;
  currentTime: number;    // Playback position in seconds
  timestamp: number;      // When state was captured (for latency compensation)
  hostId: string;         // User who initiated the action
  hostName: string;
}

// =============================================================================
// Drawing Types
// =============================================================================

export interface SelectionBox {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
}

/**
 * A single point in a stroke path
 */
export interface StrokePoint {
  x: number;
  y: number;
  /** Pressure value from 0-1 for pressure-sensitive devices */
  pressure?: number;
}

/**
 * A complete stroke path on the canvas
 */
export interface Stroke {
  id: string;
  tool: DrawingTool;
  points: StrokePoint[];
  /** Timestamp for creation time (used for fading effects) */
  createdAt?: number;
  color: string;
  strokeWidth: number;
  /** Opacity: Highlighter uses 0.4, pen/pencil use 1.0 */
  opacity: number;
}

// =============================================================================
// Reaction Types
// =============================================================================

/**
 * Available reaction emoji types
 */
export type ReactionType = '👍' | '👎' | '❤️' | '🎉' | '🔥' | '💡' | '✅' | '❓';

/**
 * A single reaction on a note
 */
export interface Reaction {
  emoji: ReactionType;
  userId: string;
  userName: string;
  timestamp: number;
}

// =============================================================================
// Comment Types
// =============================================================================

/**
 * A comment on a note, with optional nested replies
 */
export interface Comment {
  id: string;
  userId: string;
  userName: string;
  content: string;
  timestamp: string;
  resolved?: boolean;
  replies?: Comment[];

  // DB Compatibility
  parentId?: string;
  parent_id?: string;
  author_id?: string;
  author_name?: string;
  created_at?: string;
}

// =============================================================================
// Tag Types
// =============================================================================

/**
 * Available tag colors for visual categorization
 */
export type TagColor =
  | 'red'
  | 'orange'
  | 'yellow'
  | 'green'
  | 'blue'
  | 'purple'
  | 'pink'
  | 'gray';

/**
 * A tag for categorizing notes
 */
export interface Tag {
  id: string;
  name: string;
  color: TagColor;
}

// =============================================================================
// Note Sub-Types
// =============================================================================

/**
 * Table data structure for TABLE note type
 */
export interface TableData {
  headers: string[];
  rows: string[][];
}

/**
 * Planner item for PLANNER note type
 */
export interface PlannerItem {
  id: string;
  time: string;
  title: string;
  completed: boolean;
  emoji?: string;
}

/**
 * Todo item for TODO note type
 */
export interface TodoItem {
  id: string;
  text: string;
  completed: boolean;
}

/**
 * List item for LIST note type
 */
export interface ListItem {
  text: string;
  done: boolean;
}

/**
 * Flexible metadata storage
 * Using Record<string, unknown> instead of 'any' for type safety
 */
export interface NoteMeta extends Record<string, unknown> {
  // Typography
  variant?: 'plain' | 'rich' | 'markdown';
  fontSize?: number;
  fontWeight?: string;
  textAlign?: 'left' | 'center' | 'right' | 'justify';
  textColor?: string;
  fontFamily?: string;
  lineHeight?: number;
  letterSpacing?: number;
  textCase?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  textDecoration?: 'none' | 'underline' | 'line-through';

  // Shapes
  shapeType?: string;
  fillColor?: string;
  strokeColor?: string;
  strokeWidth?: number;
  strokeStyle?: 'solid' | 'dashed' | 'dotted';
  strokeCap?: 'round' | 'square' | 'butt';
  strokeJoin?: 'miter' | 'round' | 'bevel';
  cornerRadius?: number;
  rotation?: number;
  opacity?: number;
  shadow?: boolean;
  blur?: number;
  flipX?: boolean;
  flipY?: boolean;
}

// =============================================================================
// Main Note Interface
// =============================================================================

/**
 * Core note interface representing any note on the canvas
 */
export interface Note {
  id: string;
  type: NoteType;
  x: number;
  y: number;
  width?: number;
  height?: number;
  content?: string;
  title?: string;
  colorTheme?: ColorTheme;
  hidden?: boolean;
  status?: 'syncing' | 'processing' | 'error' | 'success' | null;
  statusMessage?: string;

  // Media fields
  imageUrl?: string;
  videoUrl?: string;

  // File fields
  fileName?: string;
  fileSize?: string;
  fileUrl?: string;

  // List/Todo fields
  plannerItems?: PlannerItem[];
  todoItems?: TodoItem[];
  listItems?: ListItem[];
  /** @deprecated Use listItems instead */
  items?: ListItem[];

  // Frame fields
  frameChildren?: string[];

  // Python fields
  pythonOutput?: string;

  // Table fields
  tableData?: TableData;

  // Board linking
  linkedBoardId?: string;

  // QR Code fields
  qrUrl?: string;
  qrLabel?: string;

  // Comments
  comments?: Comment[];

  // Reactions (emoji reactions for voting/feedback)
  reactions?: Reaction[];

  // Tags for categorization
  tags?: Tag[];

  // Lock state
  locked?: boolean;

  // Collaboration metadata
  lastEditedBy?: {
    userId: string;
    userName: string;
    timestamp: number;
  };

  // Flexible metadata (prefer specific fields when possible)
  meta?: NoteMeta;

  // Embed fields
  embedUrl?: string;

  // Grouping
  parentId?: string;
}

// =============================================================================
// Edge Types
// =============================================================================

/**
 * A connection between two notes
 */
export interface Edge {
  id: string;
  from: string;
  to: string;
  label?: string;
  /** Optional styling */
  style?: {
    color?: string;
    strokeWidth?: number;
    dashed?: boolean;
  };
}

// =============================================================================
// Viewport Types
// =============================================================================

/**
 * Canvas viewport state for pan and zoom
 */
export interface ViewState {
  pan: {
    x: number;
    y: number;
  };
  zoom: number;
  strokes?: Stroke[];
}

// =============================================================================
// Board Types
// =============================================================================

/**
 * A single page within a board
 * Each page has its own notes, edges, strokes, and viewport
 */
export interface Page {
  id: string;
  name: string;
  icon?: string; // Emoji or icon name
  color?: string; // Tab/Icon color (e.g. 'blue', 'red', '#ff0000')
  notes: Note[];
  edges: Edge[];
  strokes?: Stroke[];
  viewState?: ViewState;
  background?: CanvasBackground;
  createdAt?: string;
  order: number; // For reordering pages
  version?: number; // For optimistic concurrency
}

/**
 * A board containing notes, edges, and canvas state
 * Supports multiple pages for better organization
 */
export interface Board {
  id: string;
  /** Human-readable URL slug (e.g., "my-moodboard-2024"). Optional for backwards compatibility. */
  slug?: string;
  title: string;
  description?: string;
  lastEdited: string;
  isFavorite: boolean;
  isDeleted: boolean;
  themeColor?: string;

  // === Multi-page support ===
  /** Pages in this board (for multi-page boards) */
  pages?: Page[];
  /** Currently active page ID */
  currentPageId?: string;

  /** Total number of items (notes) in the board */
  itemCount?: number;

  // === Legacy single-page fields (backwards compatibility) ===
  /** @deprecated Use pages[].notes instead */
  notes: Note[];
  /** @deprecated Use pages[].edges instead */
  edges: Edge[];
  /** @deprecated Use pages[].viewState instead */
  viewState?: ViewState;
  /** @deprecated Use pages[].background instead */
  background?: CanvasBackground;
  /** @deprecated Use pages[].strokes instead */
  strokes?: Stroke[];

  /** Owner ID of the board (useful for shared board logic) */
  user_id?: string;

  /** Total notes count (from DB metadata) */
  notes_count?: number;
}

// =============================================================================
// Settings Types
// =============================================================================

/**
 * Canvas interaction settings
 */
export interface CanvasSettings {
  snapToGrid: boolean;
  showGuides: boolean;
  gridSize: number;
  showGridDots?: boolean;
  gridOpacity?: number;       // Range: 0.1 - 1.0
  gridDotScale?: number;      // Range: 0.5 - 2.0
  gridColor?: string;         // Custom hex or CSS variable
  showMajorGridLines?: boolean;
  canvasBackgroundColor?: string; // Custom background color
  /** Nudge step sizes for arrow key movement */
  nudgeAmount?: number; // Simplified nudge amount
  nudgeSteps?: {
    small: number;   // Default: 1px (Arrow keys)
    medium: number;  // Default: 10px (Shift + Arrow keys)
    large: number;   // Default: 50px (Ctrl + Arrow keys)
  };
}

// =============================================================================
// User Types
// =============================================================================

/**
 * User profile information
 */
export interface UserProfile {
  id?: string;
  name: string;
  email?: string;
  avatar?: string;
}

// =============================================================================
// Collaboration Types
// =============================================================================

export interface StickerSyncState {
  noteId: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
  manipulatorId: string;
  manipulatorName: string;
  timestamp: number;
  isActive: boolean; // true during manipulation, false on release
}

/**
 * Cursor position for collaboration
 */
export interface Cursor {
  x: number;
  y: number;
}

/**
 * A collaborator viewing/editing the board
 */
export interface Collaborator {
  id: string;
  name: string;
  color: string;
  cursor?: Cursor;
  lastActive: number;
  typingNoteId?: string;
  selectedNoteIds?: string[];
  manipulatingStickerIds?: string[];
  activeTool?: 'pointer' | 'pen' | 'hand' | 'eraser';
  currentPageId?: string; // For multi-page collaboration tracking
}

export interface BoardMember {
  boardId: string;
  userId: string;
  role: 'viewer' | 'commenter' | 'editor';
  addedAt: string;
  profile?: UserProfile;
}

// =============================================================================
// Utility Types
// =============================================================================

/**
 * Deep partial type for partial updates
 */
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/**
 * Pick with required fields
 */
export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

/**
 * Note with required position and dimensions
 */
export type PositionedNote = RequiredFields<Note, 'x' | 'y' | 'width' | 'height'>;

// =============================================================================
// Alignment Types
// =============================================================================

/**
 * Alignment guide line for dragging and snapping
 */
export interface GuideLine {
  type: 'vertical' | 'horizontal';
  position: number;
  start: number;
  end: number;
  label?: string;
}

export interface BoardState {
  notes: Note[];
  edges: Edge[];
  strokes: Stroke[];
}

// =============================================================================
// Canvas Bookmark Types
// =============================================================================

/**
 * A saved position on the canvas for quick navigation
 */
export interface CanvasBookmark {
  id: string;
  name: string;
  icon?: string;
  /** Canvas position and zoom level */
  position: {
    x: number;
    y: number;
    zoom: number;
  };
  /** Optional color for visual distinction */
  color?: string;
  /** Creation timestamp */
  createdAt: number;
  /** Optional description */
  description?: string;
}

// =============================================================================
// Navigation History Types
// =============================================================================

/**
 * A single entry in the navigation history stack
 */
export interface NavigationHistoryEntry {
  id: string;
  /** Canvas position at this point */
  position: {
    x: number;
    y: number;
    zoom: number;
  };
  /** Timestamp when this position was recorded */
  timestamp: number;
  /** Optional label (e.g., "Navigated to Note X") */
  label?: string;
}

// =============================================================================
// Saved Filter Types
// =============================================================================

/**
 * A saved filter configuration for quick access
 */
export interface SavedFilter {
  id: string;
  name: string;
  /** Icon emoji or identifier */
  icon?: string;
  /** Color for visual distinction */
  color?: string;
  /** The filter criteria */
  filters: {
    types?: string[];
    tagIds?: string[];
    tagMatchMode?: 'any' | 'all';
    searchQuery?: string;
    hasComments?: boolean;
    hasReactions?: boolean;
    hasTags?: boolean;
    colors?: string[];
    /** Date range filter (ISO string) */
    createdAfter?: string;
    createdBefore?: string;
    modifiedAfter?: string;
    modifiedBefore?: string;
  };
  /** Creation timestamp */
  createdAt: number;
  /** Last used timestamp */
  lastUsedAt?: number;
  /** Number of times this filter has been used */
  useCount?: number;
}

// =============================================================================
// Find & Replace Types
// =============================================================================

/**
 * A match found during find operation
 */
export interface FindReplaceMatch {
  noteId: string;
  /** Field where match was found */
  field: 'title' | 'content';
  /** Start index of the match in the field */
  startIndex: number;
  /** End index of the match */
  endIndex: number;
  /** The matched text */
  matchedText: string;
  /** Context around the match */
  context?: string;
}

/**
 * Find & Replace session state
 */
export interface FindReplaceState {
  query: string;
  replaceQuery: string;
  matches: FindReplaceMatch[];
  currentMatchIndex: number;
  isActive: boolean;
  isCaseSensitive: boolean;
  isWholeWord: boolean;
  isRegex: boolean;
}

// =============================================================================
// Workspace Types
// =============================================================================

/**
 * Workspace for organizing boards into groups
 * Stored locally in localStorage
 */
export interface Workspace {
  id: string;
  name: string;
  color: string;
  icon?: string;
  boardIds: string[];
  isDefault?: boolean;
  createdAt: number;
  order: number;
}

// =============================================================================
// Presentation Types (Phase 2)
// =============================================================================

/**
 * A single slide in a presentation path
 * Can reference either a page or a bookmark position
 */
export interface PresentationSlide {
  id: string;
  type: 'page' | 'bookmark';
  targetId: string;
  title?: string;
  duration?: number;
  order: number;
}

/**
 * A presentation path containing ordered slides
 * Stored in localStorage per board
 */
export interface PresentationPath {
  id: string;
  name: string;
  slides: PresentationSlide[];
  createdAt: number;
  updatedAt: number;
}

// =============================================================================
// Polling/Voting Types (Phase 2)
// =============================================================================

/**
 * A single option in a poll
 */
export interface PollOption {
  id: string;
  text: string;
  votes: string[];
}

/**
 * Poll data stored in note.meta
 */
export interface PollData {
  question: string;
  options: PollOption[];
  allowMultiple: boolean;
  isAnonymous: boolean;
  showResults: boolean;
  endsAt?: number;
  createdBy: string;
  createdAt: number;
}

// =============================================================================
// Public Link Types (Phase 2)
// =============================================================================

/**
 * A shareable public link for a board
 * Allows read-only access without authentication
 */
export interface PublicLink {
  id: string;
  boardId: string;
  token: string;
  name?: string;
  description?: string;
  customSlug?: string;
  accessMode: 'view' | 'comment' | 'edit';
  domainRestrictions?: string[];
  maxUses?: number;
  useCount: number;
  viewCount: number;
  expiresAt?: string;
  lastAccessedAt?: number;
  createdAt: number;
  createdBy: string;
  isActive: boolean;
  password?: string;
  allowedPageIds?: string[];
}